// src/pages/ApprovalsList.jsx
// v21.1 — ApprovalsList aligned with ManageRequest
// - Uses TeamBridge Approvals API via tbGetJson (paged, needCount=true)
// - Uses TB-Hinge proxy for /person/list via tbPostJson (POST body; no base64/encrypted payload)
// - No browser → Hinge direct calls
// - Keeps list state persistence (filters/sort/page/limit) and restores only on Back from detail
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AppShell from "../layout/AppShell";
import { ENV, tbGetJson, tbPostJson } from "../api/client";
import { formatDateMMDDYYYY } from "../utils/dateFormat";
import { isValidSearchQuery } from "../utils/searchValidation";
import { isLookupReady, useLookupReady } from "../hooks/useLookupReady";
import { toast } from "react-toastify";

import "./ManageRequest.css";
import "./Approvals.css";

/* ========= Constants ========= */
const CHUNK = 5000;
const MIN_PAGE_SIZE = 10;
const PREFETCH_PAGES = 3;
const MIN_SEARCH_CHARS = 4;
const GLOBAL_TOAST_CONTAINER_ID = "global";

/* ========= ENV ========= */
const APPROVALS_LIST_URL = ENV.APPROVALS_LIST_URL;
const OFFBOARD_URL = ENV.OFFBOARD_URL; // same endpoint as Manage Requests

/* ========= TB Hinge proxy (list) ========= */
const boolish = (v) => ["true", "1", "yes", "y"].includes(String(v ?? "").trim().toLowerCase());

// ✅ Same resolution / gating as ManageRequest (NO browser→Hinge direct calls)
const TB_HINGE_PERSON_LIST_URL =
    ENV.TB_HINGE_PERSON_LIST_URL ||
    ENV.TB_HINGE_PERSON_LIST ||
    ENV.TB_HINGE_LIST_URL ||
    ENV.HINGE_LIST_PROXY_URL ||
    ENV.PERSON_LIST_PROXY_URL ||
    "";

// ✅ Optional TB proxy endpoint for worker-name search (used when worker name is the *first* filter)
const TB_HINGE_SEARCH_URL =
    ENV.TB_HINGE_SEARCH_URL ||
    ENV.TB_HINGE_PERSON_SEARCH_URL ||
    ENV.HINGE_SEARCH_PROXY_URL ||
    ENV.PERSON_SEARCH_PROXY_URL ||
    "";

// ✅ TeamBridge worker-name search (npw-search-api) URL
const TB_SEARCH_URL =
    ENV.TB_SEARCH_URL ||
    ENV.TB_SEARCH_API_URL ||
    ENV.NPW_SEARCH_API_URL ||
    "";

const ENABLE_HINGE = (() => {
    const on = boolish(ENV.ENABLE_HINGE);
    return on && !!String(TB_HINGE_PERSON_LIST_URL || "").trim();
})();

/* ========= Session ========= */
const SS =
    typeof window !== "undefined" && window.sessionStorage
        ? window.sessionStorage
        : { getItem: () => "", setItem: () => { }, removeItem: () => { } };

const K = { role: "tb_role", lookup: "tb_lookup", email: "tb_email" };

/* ========= ApprovalsList state persistence ========= */
const AP_STATE_KEY = "ap.list.state.v1";
const AP_RETURN_FLAG = "ap.list.returnFromDetail.v1";

function safeParseJson(s, fallback = null) {
    try {
        if (!s) return fallback;
        return JSON.parse(s);
    } catch {
        return fallback;
    }
}
function loadApState() {
    return safeParseJson(SS.getItem(AP_STATE_KEY), null) || null;
}
function saveApState(state) {
    try {
        SS.setItem(AP_STATE_KEY, JSON.stringify(state));
    } catch { }
}
function clearApState() {
    try {
        SS.removeItem(AP_STATE_KEY);
    } catch { }
}
function markReturnFromDetail() {
    try {
        SS.setItem(AP_RETURN_FLAG, "1");
    } catch { }
}
function consumeReturnFromDetailFlag() {
    try {
        const v = SS.getItem(AP_RETURN_FLAG);
        SS.removeItem(AP_RETURN_FLAG);
        return v === "1";
    } catch {
        return false;
    }
}

/* ===== helpers ===== */
function normalizeStatus(s) {
    return String(s || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]/g, "");
}
function statusPill(status) {
    const key = normalizeStatus(status);
    const map = {
        approved: { cls: "badge badge-ok", label: "Approved" },
        rejected: { cls: "badge badge-bad", label: "Rejected" },
        inforequested: { cls: "badge badge-warn", label: "Info Requested" },
        submitted: { cls: "badge badge-blue", label: "Submitted" },
        pending: { cls: "badge badge-blue", label: "Pending" },
        inactive: { cls: "badge badge-neutral", label: "Inactive" },
        draft: { cls: "badge badge-neutral", label: "Draft" },
        active: { cls: "badge badge-ok", label: "Active" },
    };
    return map[key] || { cls: "badge badge-neutral", label: status || "-" };
}
function rowClassForStatus(status) {
    const key = normalizeStatus(status);
    if (key === "inforequested") return "row-attn";
    return "";
}
const maskSecurityId = (val) => {
    const s = String(val ?? "");
    if (!s) return "-";
    return s.length <= 4 ? "*".repeat(s.length) : "*".repeat(s.length - 4) + s.slice(-4);
};
function Notice({ type = "info", message, onClose }) {
    if (!message) return null;
    return (
        <div className={`notice ${type}`}>
            <div className="notice-msg">{message}</div>
            <button className="notice-x" onClick={onClose} aria-label="close">
                ×
            </button>
        </div>
    );
}
function asDateStr(d) {
    if (!d) return "";
    const s = String(d);
    if (s.includes("T")) return s.split("T")[0] || s;
    return s;
}
function splitContractPo(val) {
    const cpo = String(val || "").trim();
    if (!cpo) return { contract_number: "", po_line: "" };
    const parts = cpo.split("/").map((p) => p.trim());
    return { contract_number: parts[0] || "", po_line: parts.slice(1).join("/") || "" };
}

const dedupeCommentLines = (txt) => {
    const raw = String(txt || "");
    const lines = raw.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const line of lines) {
        const key = line.replace(/\s+/g, " ").toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(line);
    }
    return out.join("\n").trim();
};

const normalizeCommentText = (v) => {
    if (v == null) return "";
    if (Array.isArray(v)) {
        const joined = v
            .map((x) => (x == null ? "" : String(x)))
            .map((s) => s.trim())
            .filter(Boolean)
            .join("\n");
        return dedupeCommentLines(joined);
    }
    return dedupeCommentLines(String(v).trim());
};

const mergeCommentsUnique = (a, b) => {
    const aa = normalizeCommentText(a);
    const bb = normalizeCommentText(b);
    if (!aa) return bb;
    if (!bb) return aa;
    if (aa === bb) return aa;
    if (aa.includes(bb)) return aa;
    if (bb.includes(aa)) return bb;
    return `${aa}\n${bb}`.trim();
};

/* ===== Actor helpers (match Manage Requests behavior) ===== */
function getVendor() {
    const vendorId = SS.getItem("tb_vendorId") || "";
    const supplierName = SS.getItem("tb_supplierName") || "";
    return (vendorId || supplierName || "").trim();
}
function getActorDtvId() {
    return SS.getItem("tb_dtvid") || SS.getItem(K.email) || "";
}
function getActorEmail() {
    return SS.getItem("tb_email") || SS.getItem(K.email) || "";
}
function getActorName() {
    return (SS.getItem("tb_name") || "").trim();
}
function extractOffboardError(err) {
    if (!err) return "Offboard failed.";
    const msg = err?.message || "";
    return String(msg || "Offboard failed.");
}

/* ===== Columns ===== */
// NOTE: Per latest UI request, Approvals list hides Contract/PO/ID columns.
const COLUMNS = [
    { field: "full_name", label: "Worker Name", isName: true },
    { field: "vendor", label: "Vendor" },
    { field: "status", label: "Status" },
    { field: "start_date", label: "Start Date" },
    { field: "end_date", label: "End Date" },
];

const COLUMNS_EXPORT = [
    { field: "full_name", label: "Worker Name", isName: true },
    { field: "vendor", label: "Vendor" },
    { field: "status", label: "Status" },
      { field: "dtvuid", label: "DTV ID" },
      { field: "sponsor", label: "Sponsor" },
      { field: "manager", label: "Webphone Manager" },
    { field: "start_date", label: "Start Date" },
    { field: "end_date", label: "End Date" },
];

/* ===== Small pill button for masked ID ===== */
function IdPillButton({ text, disabled, onClick, title }) {
    const base = {
        display: "inline-block",
        padding: "4px 10px",
        borderRadius: "9999px",
        background: "#eaf2ff",
        color: "#1e5bb8",
        fontWeight: 600,
        letterSpacing: "0.3px",
        lineHeight: 1,
        border: "none",
        boxShadow: "inset 0 0 0 1px rgba(30,91,184,0.12)",
        cursor: disabled ? "default" : "pointer",
        textDecoration: "none",
        outline: "none",
        whiteSpace: "nowrap",
    };
    return (
        <button
            type="button"
            title={title}
            aria-label={title}
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            style={base}
        >
            {text}
        </button>
    );
}

/* ===== Role helpers (aligned with Manage Requests) ===== */
function attachRoleParams(params) {
    const storedRole = SS.getItem(K.role) || "Nominator";
    const rawRole = storedRole.trim();
    const roleKey = rawRole.toLowerCase();

    const lookupRaw = SS.getItem(K.lookup) || "{}";
    let lookup = {};
    try {
        lookup = JSON.parse(lookupRaw);
    } catch {
        lookup = {};
    }

    const email = SS.getItem(K.email) || "";
    const dtvId = lookup?.dtvAnchorId || lookup?.primaryEmail || SS.getItem("tb_dtvid") || "";
    const vendor = getVendor();
    const sponsorId = (dtvId || email || "").trim();

    params.set("role", rawRole);
    params.set("loggedInUserRole", rawRole);

    if (roleKey === "nominator") {
        if (vendor) params.set("vendor", vendor);
        if (email) params.set("id", email);
    } else if (roleKey === "employee supplier admin" || roleKey === "employeesupplier admin") {
        if (email) params.set("id", email);
    } else if (roleKey === "fte nominator") {
        const id = dtvId || email;
        if (id) params.set("id", id);
    } else if (roleKey === "employee sponsor") {
        if (sponsorId) params.set("sponsor", sponsorId);
    } else if (roleKey === "cp channel delegate") {
        const cpDelegateDtvId = String(SS.getItem("tb_dtvid") || dtvId || email || "").trim();
        if (cpDelegateDtvId) params.set("id", cpDelegateDtvId);
        if (cpDelegateDtvId) params.set("dtvid", cpDelegateDtvId);
    } else if (roleKey === "dtv ops admin") {
        // no extras
    } else {
        const id = email || dtvId;
        if (id) params.set("id", id);
    }
    return params;
}

/* ===== Request memoization ===== */
const TB_PROMISES = new Map();
const HINGE_PROMISES = new Map();

function memoizeOnce(map, key, factory) {
    if (map.has(key)) return map.get(key);
    const p = (async () => {
        try {
            return await factory();
        } catch (e) {
            // Never memoize failures: a transient error during navigation would otherwise be
            // reused by the next mount and show "no results" until a full page reload.
            if (map.get(key) === p) map.delete(key);
            throw e;
        } finally {
            setTimeout(() => {
                if (map.get(key) === p) map.delete(key);
            }, 8000);
        }
    })();
    map.set(key, p);
    return p;
}

function stableJSONStringifyNoCorr(obj) {
    const flat = [];
    const recur = (node, prefix = "") => {
        if (node && typeof node === "object" && !Array.isArray(node)) {
            Object.keys(node)
                .sort()
                .forEach((k) => {
                    if (k === "correlationId") return;
                    recur(node[k], prefix ? `${prefix}.${k}` : k);
                });
        } else {
            flat.push([prefix, node]);
        }
    };
    recur(obj);
    return JSON.stringify(flat);
}

/* ===== Hinge helpers ===== */
function buildHingeSearchCriteriaNoMonth(storedRole, dtvId, supplierName) {
    const roleKey = (storedRole || "").toLowerCase();
    if (roleKey === "nominator") return { roleName: "Nominator", userVendorName: supplierName || "" };
    if (roleKey === "employeesupplier admin" || roleKey === "employee supplier admin")
        return { roleName: "Employee Supplier Admin" /*, nominatorDtvId: dtvId || ""*/ };
    if (roleKey === "fte nominator") return { roleName: "FTE Nominator", nominatorDtvId: dtvId || "" };
    if (roleKey === "employee sponsor") return { roleName: "Employee Sponsor", approverDtvId: dtvId || "" };
    if (roleKey === "cp channel delegate")
        return { roleName: "CP Channel Delegate", isCPChannelDelegate: true, attuidApproverDtvId: dtvId || "" };
    if (roleKey === "dtv ops admin" || (!storedRole && !dtvId)) return { roleName: storedRole || "DTV OPS Admin" };
    return { roleName: storedRole };
}

function buildHingeSearchIdentityParams(roleName, dtvId, supplierName) {
    const role = String(roleName || "").trim();
    const roleKey = role.toLowerCase();
    const id = String(dtvId || "").trim();
    const vendor = String(supplierName || "").trim();
    const out = {};

    if (roleKey === "nominator") {
        if (role) {
            out.role = role;
            out.searcherRoleName = role;
        }
        if (vendor) {
            out.vendor = vendor;
            out.searcherSupplierName = vendor;
        }
        if (id) out.searchDtvId = id;
        return out;
    }

    if (roleKey === "employee sponsor") {
        if (role) {
            out.role = role;
            out.loggedInUserRole = role;
            out.searcherRoleName = role;
        }
        if (id) {
            out.searchDtvId = id;
            out.sponsor = id;
            out.approverDtvId = id;
        }
        return out;
    }

    if (roleKey === "cp channel delegate") {
        if (role) {
            out.role = role;
            out.loggedInUserRole = role;
            out.searcherRoleName = role;
        }
        if (id) {
            out.searchDtvId = id;
            out.approverDtvId = id;
            out.attuidApproverDtvId = id;
        }
        return out;
    }

    // Always send the caller's role for any remaining role
    // (e.g. Employee Supplier Admin, FTE Nominator, DTV OPS Admin).
    if (role) {
        out.role = role;
        out.searcherRoleName = role;
    }
    if (id) out.searchDtvId = id;
    return out;
}

function unwrapTbEnvelope(res) {
    // TB-hinge proxy wraps upstream payload under `response`
    return res?.response ?? res?.data ?? res?.content ?? res;
}

function mapTBRowForUI(r) {
    const first_name = r?.first_name || r?.firstName || "";
    const last_name = r?.last_name || r?.lastName || "";
    const full_name = `${first_name} ${last_name}`.trim() || r?.full_name || r?.name || "-";

    const start_date = asDateStr(r?.start_date || r?.startDate || r?.effectiveStartDate || r?.startDateTs || r?.start_dt);
    const end_date = asDateStr(r?.end_date || r?.endDate || r?.effectiveEndDate || r?.endDateTs || r?.end_dt);

    const created_at = r?.created_at || r?.createdAt || r?.createdTs || r?.created || r?.updated_at || r?.updatedAt || "";
    const updated_at = r?.updated_at || r?.updatedAt || r?.modified_at || r?.modifiedAt || r?.lastUpdated || created_at;

    return {
        ...r,
        npw_id: r?.npw_id || r?.npwId || r?.npwID || r?.id || r?.npwId,
        first_name,
        last_name,
        full_name,
        vendor: r?.vendor || r?.supplierName || r?.supplier_name || r?.npw_manager_company || "",
        status: r?.status || r?.personStatus || r?.approval_status || "",
        dtvuid: r?.dtvAnchorId || r?.dtvUid || r?.dtvid || r?.dtvId || r?.dtvuid || "",
        sponsor: r?.employeeSponsor || r?.sponsor || "",
        manager: r?.supervisorDtvAnchorId || r?.manager ||  r?.webphone_manager_id || "",
        start_date,
        end_date,
        created_at,
        updated_at,
    };
}


function normalizeHingeList(res) {
    const x = unwrapTbEnvelope(res);
    if (!x) return [];
    if (Array.isArray(x)) return x;

    // TB proxy / upstream common shapes
    if (Array.isArray(x.personList)) return x.personList;
    if (Array.isArray(x.persons)) return x.persons;

    // legacy fallbacks (keep for safety)
    if (Array.isArray(res?.personList)) return res.personList;
    if (Array.isArray(res?.data?.personList)) return res.data.personList;
    if (Array.isArray(res?.content?.personList)) return res.content.personList;
    if (Array.isArray(res?.data?.persons)) return res.data.persons;
    if (Array.isArray(res?.content?.persons)) return res.content.persons;

    return [];
}
function extractHingeTotal(res, listLength) {
    const x = unwrapTbEnvelope(res);
    const v =
        x?.totalRecordCount ??
        x?.total ??
        x?.pagination?.totalRecords ??
        x?.pageMetadata?.total ??
        listLength ??
        0;

    const n = Number(v);
    return Number.isFinite(n) ? n : (listLength ?? 0);
}
function makeCorrelationId() {
    try {
        if (typeof window !== "undefined" && window.crypto?.randomUUID) return window.crypto.randomUUID();
    } catch { }
    return `req_${Date.now()}`;
}

/* ===== Hinge page fetch (NOW via TB proxy) ===== */
async function fetchHingePage({ page = 1, pageSize = 10, roleName, dtvId, supplierName }) {
    if (!ENABLE_HINGE) return { list: [], total: 0 };

    // ✅ Same POST body shape as ManageRequest (NO encrypted/base64 payload)
    const base = String(TB_HINGE_PERSON_LIST_URL || "").trim().replace(/\/\/+$/, "");
    if (!base) return { list: [], total: 0 };

    const searchCriteria = buildHingeSearchCriteriaNoMonth(roleName, dtvId, supplierName);

    const body = {
        correlationId: makeCorrelationId(),
        sourceSystemCode: "1",
        searchCriteria,
        pagination: { pageNumber: String(page), pageSize: String(pageSize) },
    };

    // Memoize by endpoint + stable body (excluding correlationId)
    const key = `${base}|${stableJSONStringifyNoCorr(body)}`;

    const res = await memoizeOnce(HINGE_PROMISES, key, async () => tbPostJson(base, body));

    const list = normalizeHingeList(res);
    const total = extractHingeTotal(res, list.length);

    return { list, total: Number(total || 0) };
}

/* ===== TeamBridge paged fetch (Approvals list) ===== */
async function fetchHingeSearchPage({ q = "", page = 1, pageSize = 10, roleName, dtvId, supplierName }) {
    const base = String(TB_HINGE_SEARCH_URL || "").trim();
    if (!base) return { list: [], total: 0 };

    const params = new URLSearchParams();
    params.set("q", String(q || "").trim());
    const identityParams = buildHingeSearchIdentityParams(roleName, dtvId, supplierName);
    Object.entries(identityParams).forEach(([k, v]) => {
        if (v != null && String(v).trim()) params.set(k, String(v).trim());
    });
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));

    const url = base.includes("?") ? `${base}&${params.toString()}` : `${base}?${params.toString()}`;
    const data = await tbGetJson(url);
    const payload = data?.response ?? data ?? {};
    const total = Number(payload.totalRecordCount || payload.total || payload.count || 0) || 0;
    const list = payload.personList || payload.personDetails || payload.items || payload.results || payload.data || [];
    return { list: Array.isArray(list) ? list : [], total };
}

async function fetchTBSearchOnce({ q }) {
    const base = (TB_SEARCH_URL || "").replace(/\/$/, "");
    if (!base) throw new Error("TB_SEARCH_URL missing");
    const params = new URLSearchParams({ npwId: String(q || ""), action: "all" });
    attachRoleParams(params);
    const url = `${base}?${params.toString()}`;
    const res = await memoizeOnce(TB_PROMISES, url, async () => tbGetJson(url));
    const arr = Array.isArray(res?.data) ? res.data : Array.isArray(res?.rows) ? res.rows : [];
    return { rows: arr.map(mapTBRowForUI), total: arr.length };
}

async function fetchTBPage({ page = 1, limit = 10, sortField = "updated_at", sortOrder = "desc" }) {
    const base = (APPROVALS_LIST_URL || "").replace(/\/$/, "");
    if (!base) throw new Error("APPROVALS_LIST_URL missing");

    const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        sortField: String(sortField),
        sortOrder: String(sortOrder),
        needCount: "true",
        approvalPage: "true",
    });
    attachRoleParams(params);

    const url = `${base}?${params.toString()}`;
    const res = await memoizeOnce(TB_PROMISES, url, async () => tbGetJson(url));

    const tbRows = Array.isArray(res?.rows) ? res.rows : [];
    const mapped = tbRows.map((r) => {
        const full_name = `${r.first_name || ""} ${r.last_name || ""}`.trim();
        const split = splitContractPo(r.contract_po);
        const mergedComments = mergeCommentsUnique(r.comments, r.comment || r.notes);
        return {
            _source: "TB",
            ...r,
            // Normalize identity + id fields so rowKey/merge stays stable across API shapes
            npw_id: r?.npw_id || r?.npwId || r?.npwID || r?.id || "",
            dtvuid: r?.dtvuid || r?.dtvUid || r?.dtvId || r?.dtvAnchorId || "",
            attuid: r?.attuid || r?.attUid || "",
            email: r?.email || r?.primaryEmail || r?.secondaryEmail || "",
            comments: mergedComments || r.comments || r.comment || r.notes || "",
            full_name,
            contract_number: r.contract_number || split.contract_number,
            po_num: r.po_num || r.po_line || split.po_line || "",
            vendor: r.vendor || r.npw_manager_company || "",
        };
    });

    const total = Number(res?.total ?? mapped.length ?? 0);
    return { rows: mapped, total };
}

/* ===== Row mapper for Hinge -> approvals list row ===== */
function mapHingePersonToRow(p) {
    const first = p?.firstName ?? p?.first_name ?? "";
    const last = p?.lastName ?? p?.last_name ?? "";
    const full_name =
        `${first || ""} ${last || ""}`.trim() ||
        p?.full_name ||
        p?.name ||
        p?.dtvAnchorId ||
        p?.attuid ||
        "-";

    const created_at = p?.createdTs || p?.created_at || p?.createdAt || "";
    const updated_at = p?.updated_at || p?.updatedAt || p?.modifiedTs || p?.modified_at || p?.lastUpdated || created_at;

    return {
        _source: "HINGE",
        npw_id: undefined,

        contract_number: p?.contractNumber || p?.contract_number || "-",
        po_num: p?.poNumber || p?.po_num || "-",

        vendor: p?.supplierName || p?.vendor || p?.supplier_name || "-",
        first_name: first || "",
        last_name: last || "",
        full_name,

        role: p?.dtvRoleName || p?.role || "",
        status: p?.status || p?.personStatus || p?.approval_status || "",

        // Hinge Search may return createdTs only; list API may return effective dates
        start_date: asDateStr(p?.effectiveStartDate || p?.start_date || p?.startDate || p?.startDt || ""),
        end_date: asDateStr(p?.effectiveEndDate || p?.end_date || p?.endDate || p?.endDt || ""),
        created_at,
        updated_at,

        email: p?.primaryEmail || p?.secondaryEmail || p?.email || "",
        dtvuid: p?.dtvAnchorId || p?.dtvUid || p?.dtvid || p?.dtvId || p?.dtvuid || "",
        attuid: p?.attuid || p?.attUid || "",

        att_cp_role: p?.cinglepointRoleId || p?.att_cp_role || "",
        person_type: p?.personType || p?.person_type || "",
        sponsor: p?.employeeSponsor || p?.sponsor || "",
        manager: p?.supervisorDtvAnchorId || p?.manager ||  p?.webphone_manager_id || "",
        line: p?.lineNumber || p?.line || "",

        security_id: "-",
    };
}


/* ===== Component ===== */
export default function ApprovalsList() {
    const navigate = useNavigate();
    const location = useLocation();

    const lookupReady = useLookupReady();

    const pathname = (location && location.pathname) || "";
    const isListRoute = pathname === "/approvals";

    // ---- restore gating ----
    const restoredOnceRef = useRef(false);
    const saved = loadApState();

    // backing window (merged TB + Hinge)
    const [rows, setRows] = useState([]);
    const rowsRef = useRef([]);
    useEffect(() => {
        rowsRef.current = rows || [];
    }, [rows]);
    const [fullRows, setFullRows] = useState(null);

    // totals
    const tbTotalRef = useRef(0);
    const hingeTotalRef = useRef(0);
    const cachedWindowRef = useRef(0);
    // Resume paging from the next unfetched page (with a fixed page size) instead of
    // re-requesting page 1 with an ever-growing limit. Re-requesting page 1 each time the
    // window grows re-loads the whole Hinge list repeatedly and, for tied sort values, can
    // come back in a slightly different order — making rows appear to shuffle between pages
    // during the initial load.
    const tbNextPageRef = useRef(1);
    const hingeNextPageRef = useRef(1);
    const tbPageSizeRef = useRef(0);
    const hingePageSizeRef = useRef(0);
    const resetPagingCursors = () => {
        tbNextPageRef.current = 1;
        hingeNextPageRef.current = 1;
        tbPageSizeRef.current = 0;
        hingePageSizeRef.current = 0;
    };
    // Freezes a stable global row order (per sort/filter sig, independent of page size) so rows
    // never reshuffle as the list streams in or when rows-per-page changes — see clientPagedRows.
    const lockedPagesRef = useRef({ sig: "", keys: [] });
    const [allRowsLoaded, setAllRowsLoaded] = useState(false);

    // ✅ Defaults
    const [page, setPage] = useState(saved?.page && Number(saved.page) > 0 ? Number(saved.page) : 1);
    const [limit, setLimit] = useState(saved?.limit && Number(saved.limit) >= MIN_PAGE_SIZE ? Number(saved.limit) : MIN_PAGE_SIZE);
    const [sortField, setSortField] = useState(saved?.sortField || "updated_at");
    const [sortOrder, setSortOrder] = useState(saved?.sortOrder || "desc");

    const [loading, setLoading] = useState(false);
    // When we have shown *some* data, remaining pages continue to load in background.
    const [loadingMore, setLoadingMore] = useState(false);
    // Counts genuinely in-flight background loads so the "Loading more" banner never gets
    // stuck true when the effect re-runs (and cancels the previous run) while data streams in.
    const loadMoreInflightRef = useRef(0);
    const [selected, setSelected] = useState([]);
    const [notice, setNotice] = useState(null);

    /* ===== Role-based Manage actions on Approvals page ===== */
    const userRole = (SS.getItem(K.role) || "").trim();
    const CAN_MANAGE_ON_APPROVALS = new Set(["Employee Sponsor", "Employee Supplier Admin", "DTV OPS Admin"]);
    const canManageHere = CAN_MANAGE_ON_APPROVALS.has(userRole);

    // Offboard modal state (mirrors Manage Requests)
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [endDate, setEndDate] = useState("");
    const [note, setNote] = useState("");
    const [offboardIdType, setOffboardIdType] = useState("DTVID");
    const [offBoardError, setOffboardError] = useState("");
    const [offBoardServerError, setOffboardServerError] = useState("");
    const [offboarding, setOffboarding] = useState(false);

    // filters/search
    const [filters, setFilters] = useState(saved?.filters && typeof saved.filters === "object" ? saved.filters : {});
    const [filterInputValues, setFilterInputValues] = useState(saved?.filters && typeof saved.filters === "object" ? saved.filters : {});

    useEffect(() => {
        setFilterInputValues((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((k) => {
                if (!(k in (filters || {}))) delete next[k];
            });
            Object.entries(filters || {}).forEach(([k, v]) => {
                next[k] = String(v ?? "");
            });
            return next;
        });
    }, [filters]);

    // debounce filter changes so we don't call APIs on every keystroke
    const [debouncedFilters, setDebouncedFilters] = useState(filterInputValues);
    const debounceTimerRef = useRef(null);

    useEffect(() => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            setDebouncedFilters(filterInputValues);
        }, 650);
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, [filterInputValues]);

    const debouncedFiltersClean = useMemo(() => {
        const f = { ...(debouncedFilters || {}) };
        const min4Fields = new Set(["full_name", "vendor"]);
        for (const k of Object.keys(f)) {
            const v = String(f[k] || "").trim();
            if (min4Fields.has(k) && v && (v.length < 4 || !isValidSearchQuery(v))) f[k] = "";
            else f[k] = v;
        }
        return f;
    }, [debouncedFilters]);

    const filterInputRefs = useRef({});
    const firstFilterKeyRef = useRef("");
    const lastEditedFilterKeyRef = useRef("");
    const searchSignatureRef = useRef("");
    const searchPrimedRef = useRef(false);
    const prevFilterCountRef = useRef(0);
    const searchLoadIdRef = useRef(0);

    const rowKey = (r) => {
        const npw = r?.npw_id || r?.npwId || r?.npwID || r?.id;
        if (npw != null && String(npw).trim()) return `NPW:${String(npw).trim()}`;

        const dtv = r?.dtvuid || r?.dtvUid || r?.dtvId || r?.dtvAnchorId;
        if (dtv != null && String(dtv).trim()) return `DTV:${String(dtv).trim()}`;

        const att = r?.attuid || r?.attUid;
        if (att != null && String(att).trim()) return `ATT:${String(att).trim().toUpperCase()}`;

        const email = r?.email || r?.primaryEmail || r?.secondaryEmail;
        if (email != null && String(email).trim()) return `EMAIL:${String(email).trim().toLowerCase()}`;

        const fallback = `${r?.first_name || ""}|${r?.last_name || ""}|${r?.contract_number || ""}|${r?.po_num || ""}`;
        return `ROW:${fallback}`;
    };

    const personKey = (r) => {
        const dtv = r?.dtvuid || r?.dtvUid || r?.dtvId || r?.dtvAnchorId;
        if (dtv != null && String(dtv).trim()) return `DTV:${String(dtv).trim()}`;
        const att = r?.attuid || r?.attUid;
        if (att != null && String(att).trim()) return `ATT:${String(att).trim().toUpperCase()}`;
        const email = r?.email || r?.primaryEmail || r?.secondaryEmail;
        if (email != null && String(email).trim()) return `EMAIL:${String(email).trim().toLowerCase()}`;
        return "";
    };

    const mergeUniqueByKey = (baseArr, addArr) => {
        /**
         * Simple merge: only de-dupe exact duplicates, show ALL records from both sources
         * De-dupe rules:
         * 1. By rowKey() - same exact record
         * 2. Hinge↔Hinge by personKey() - avoid Hinge duplicates of same person
         * NO status-based filtering - show all records
         */
        const out = [];
        const seenRow = new Set();
        const hingeSeenPerson = new Set(); // Only de-dupe Hinge-to-Hinge repeats

        const pushRow = (r) => {
            const rk = rowKey(r);
            if (seenRow.has(rk)) return; // Skip exact duplicates

            const src = String(r?._source || "").toUpperCase();
            const isHinge = src === "HINGE";
            const pk = personKey(r);

            // Only de-dupe multiple Hinge records for the same person
            if (isHinge && pk) {
                if (hingeSeenPerson.has(pk)) return; // Skip Hinge duplicates
                hingeSeenPerson.add(pk);
            }

            out.push(r);
            seenRow.add(rk);
        };

        for (const r of baseArr || []) pushRow(r);
        for (const r of addArr || []) pushRow(r);
        return out;
    };




    // When progressive paging: if fullRows is not yet populated but rows already has data,
    // seed fullRows from current rows before merging new pages.
    const mergeIntoFullRows = (addArr) => {
        setFullRows((prev) => {
            const base = prev && prev.length ? prev : rowsRef.current || [];
            return mergeUniqueByKey(base, addArr || []);
        });
    };

    /* ===== Persist list state whenever it changes (only on list route) ===== */
    useEffect(() => {
        if (!isListRoute) return;
        saveApState({ page, limit, sortField, sortOrder, filters });
    }, [isListRoute, page, limit, sortField, sortOrder, debouncedFiltersClean]);

    /* ===== Restore ONLY if we are returning from detail; otherwise CLEAR ===== */
    useEffect(() => {
        if (!isListRoute) return;
        if (restoredOnceRef.current) return;
        restoredOnceRef.current = true;

        const fromDetail = consumeReturnFromDetailFlag();
        if (!fromDetail) {
            // user came from another tab/page (not back from detail) -> clear search UI state
            clearApState();
            setFilters({});
            setPage(1);
            setLimit(MIN_PAGE_SIZE);
            setSortField("updated_at");
            setSortOrder("desc");
            setFullRows(null);
            searchPrimedRef.current = false;
            prevFilterCountRef.current = 0;
            return;
        }

        // returning from detail -> restore saved state
        const s = loadApState();
        if (s) {
            if (s.page && Number(s.page) > 0) setPage(Number(s.page));
            if (s.limit && Number(s.limit) >= MIN_PAGE_SIZE) setLimit(Number(s.limit));
            if (s.sortField) setSortField(String(s.sortField));
            if (s.sortOrder) setSortOrder(String(s.sortOrder));
            if (s.filters && typeof s.filters === "object") setFilters(s.filters);
        }
    }, [isListRoute]);

    /* ===== Toast/notice from navigation (optional) ===== */
    useEffect(() => {
        const n = location.state && location.state.notice;
        if (n) {
            setNotice(n);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, navigate]);

    /* ===== Default window fetch (lazy) ===== */
    const fetchDefaultWindow = async (sizeNeeded) => {
        // Delay hinge-list / approvals APIs until the lookup API has responded.
        // Callers re-run via the `lookupReady` effect dep once lookup is ready. No role checks.
        if (!isLookupReady()) return;
        setLoading(true);
        try {
            const lookupRaw = SS.getItem(K.lookup) || "{}";
            let prof = {};
            try {
                prof = JSON.parse(lookupRaw);
            } catch {
                prof = {};
            }
            const storedRole = (SS.getItem(K.role) || "Nominator").trim();
            const dtvId =
                prof?.dtvAnchorId ||
                SS.getItem("tb_dtvid") ||
                SS.getItem(K.email) ||
                ENV.DEFAULT_DTVID ||
                "";
            const supplierName = prof?.supplierName || getVendor();

            // Prefer TB for the first render so the initial page is truly "last updated".
            // Hinge is merged in once TB settles; if TB is empty/fails, fall back to Hinge.
            let shownAny = false;
            let tbFinished = false;
            let tbHadRows = false;
            let hingeBuffered = [];
            let currentWindowRows = rowsRef.current || [];
            const showIfNeeded = () => {
                if (shownAny) return;
                shownAny = true;
                setLoading(false);
            };
            const appendRowsToWindow = (newRows) => {
                if (!newRows || !newRows.length) return;
                currentWindowRows = mergeUniqueByKey(currentWindowRows, newRows);
                if (currentWindowRows.length > (rowsRef.current || []).length) {
                    showIfNeeded();
                    setRows(currentWindowRows);
                    cachedWindowRef.current = Math.max(cachedWindowRef.current, currentWindowRows.length);
                }
            };
            const flushBufferedHinge = () => {
                if (!hingeBuffered.length) return;
                appendRowsToWindow(hingeBuffered);
                hingeBuffered = [];
            };

            const p = [];
            // Use a fixed page size for the lifetime of this loading session (established once
            // on the first fetch) and resume from the next unfetched page each time the window
            // needs to grow. This avoids re-requesting page 1 with an ever-increasing limit.
            if (!tbPageSizeRef.current) {
                tbPageSizeRef.current = Math.max(MIN_PAGE_SIZE, Math.min(CHUNK, limit * PREFETCH_PAGES));
            }
            if (!hingePageSizeRef.current) {
                hingePageSizeRef.current = Math.max(MIN_PAGE_SIZE, Math.min(CHUNK, limit * PREFETCH_PAGES));
            }
            const tbPageSize = tbPageSizeRef.current;
            const hingePageSize = hingePageSizeRef.current;
            let currentTbPage = tbNextPageRef.current;
            let effectiveTbPageSize = tbPageSize;
            let currentHingePage = hingeNextPageRef.current;
            let effectiveHingePageSize = hingePageSize;

            p.push(
                (async () => {
                    try {
                        const firstPage = await fetchTBPage({ page: currentTbPage, limit: tbPageSize, sortField, sortOrder });
                        if (!tbTotalRef.current) tbTotalRef.current = Number(firstPage.total || 0);
                        let mappedTB = (firstPage.rows || []).map(mapTBRowForUI);
                        if (mappedTB.length) {
                            tbHadRows = true;
                            effectiveTbPageSize = mappedTB.length;
                            if (hingeBuffered.length) {
                                mappedTB = mergeUniqueByKey(mappedTB, hingeBuffered);
                                hingeBuffered = [];
                            }
                            appendRowsToWindow(mappedTB);
                        }

                        const tbTotal = Number(tbTotalRef.current || 0);
                        const tbPages = effectiveTbPageSize > 0 ? Math.max(1, Math.ceil(tbTotal / effectiveTbPageSize)) : 1;
                        currentTbPage += 1;

                        while (!tbFinished && currentWindowRows.length < sizeNeeded && currentTbPage <= tbPages) {
                            const pageResult = await fetchTBPage({ page: currentTbPage, limit: tbPageSize, sortField, sortOrder });
                            const pageRows = (pageResult.rows || []).map(mapTBRowForUI);
                            if (!pageRows.length) break;
                            effectiveTbPageSize = Math.max(effectiveTbPageSize, pageRows.length);
                            appendRowsToWindow(pageRows);
                            currentTbPage += 1;
                        }
                    } finally {
                        tbFinished = true;

                        // If TB returned empty (or failed) but Hinge already has data, show Hinge now.
                        if (!tbHadRows && hingeBuffered.length) {
                            flushBufferedHinge();
                        }
                    }
                })()
            );

            if (ENABLE_HINGE) {
                p.push(
                    (async () => {
                        const firstPage = await fetchHingePage({ page: currentHingePage, pageSize: hingePageSize, roleName: storedRole, dtvId, supplierName });
                        if (!hingeTotalRef.current) hingeTotalRef.current = Number(firstPage.total || 0);
                        const mappedHinge = (firstPage.list || []).map(mapHingePersonToRow);
                        if (mappedHinge.length) {
                            effectiveHingePageSize = mappedHinge.length;
                            if (!tbFinished) {
                                hingeBuffered = mergeUniqueByKey(hingeBuffered, mappedHinge);
                            } else {
                                appendRowsToWindow(mappedHinge);
                            }
                        }

                        const hingeTotal = Number(hingeTotalRef.current || 0);
                        const hingePages = effectiveHingePageSize > 0 ? Math.max(1, Math.ceil(hingeTotal / effectiveHingePageSize)) : 1;
                        currentHingePage += 1;

                        while (currentWindowRows.length < sizeNeeded && currentHingePage <= hingePages) {
                            if (tbFinished && hingeBuffered.length) {
                                flushBufferedHinge();
                            }

                            const pageResult = await fetchHingePage({ page: currentHingePage, pageSize: hingePageSize, roleName: storedRole, dtvId, supplierName });
                            const pageRows = (pageResult.list || []).map(mapHingePersonToRow);
                            if (!pageRows.length) break;
                            effectiveHingePageSize = Math.max(effectiveHingePageSize, pageRows.length);
                            if (!tbFinished) {
                                hingeBuffered = mergeUniqueByKey(hingeBuffered, pageRows);
                                currentHingePage += 1;
                                continue;
                            }

                            appendRowsToWindow(pageRows);
                            currentHingePage += 1;
                        }

                        if (tbFinished && hingeBuffered.length) {
                            flushBufferedHinge();
                        }
                    })()
                );
            }

            const settled = await Promise.allSettled(p);
            const hadAnyFulfilled = settled.some((s) => s.status === "fulfilled");

            // Persist how far we've paged so the next call resumes instead of restarting.
            tbNextPageRef.current = currentTbPage;
            hingeNextPageRef.current = currentHingePage;

            const tbTotal = Number(tbTotalRef.current || 0);
            const hingeTotal = Number(hingeTotalRef.current || 0);
            const tbFullyLoaded = currentTbPage > Math.max(1, Math.ceil(tbTotal / Math.max(1, effectiveTbPageSize)));
            const hingeFullyLoaded = !ENABLE_HINGE || currentHingePage > Math.max(1, Math.ceil(hingeTotal / Math.max(1, effectiveHingePageSize)));
            if (tbFullyLoaded && hingeFullyLoaded) {
                setAllRowsLoaded(true);
            }

            if (!shownAny) {
                setLoading(false);
                if (!hadAnyFulfilled) setNotice({ type: "error", message: "Failed to load approvals." });
            }

            searchPrimedRef.current = false;
            setFullRows(null);
        } catch (e) {
            console.error("Approvals default window fetch failed:", e);
            setNotice({ type: "error", message: "Failed to load approvals." });
        } finally {
            setLoading(false);
        }
    };

    // Initial and subsequent default paging — only fetch when needed (no filters)
    useEffect(() => {
        if (!isListRoute) return;

        const activeFilterCount = Object.values(filters).filter((v) => String(v || "").trim()).length;
        if (activeFilterCount > 0) return;

        // Once both sources are fully loaded, never re-trigger a fetch for this sig —
        // otherwise a persistent mismatch between the deduped row count and the raw combined
        // total could keep re-invoking fetchDefaultWindow indefinitely.
        if (allRowsLoaded) return;

        const windowNeeded = Math.max(MIN_PAGE_SIZE, page * limit);
        if (rows.length >= windowNeeded) return;

        // Grow the window in chunks (a few pages at a time) so we don't call APIs on every page click.
        const quantum = Math.max(MIN_PAGE_SIZE, limit * PREFETCH_PAGES);
        const quantizedNeeded = Math.max(MIN_PAGE_SIZE, Math.ceil(windowNeeded / quantum) * quantum);

        const combinedTotal = (tbTotalRef.current || 0) + (hingeTotalRef.current || 0);
        const targetSize = combinedTotal ? Math.min(quantizedNeeded, combinedTotal) : quantizedNeeded;
        if (cachedWindowRef.current >= targetSize) return;

        let cancelled = false;
        (async () => {
            loadMoreInflightRef.current += 1;
            setLoadingMore(true);
            try {
                await fetchDefaultWindow(targetSize);
            } finally {
                loadMoreInflightRef.current = Math.max(0, loadMoreInflightRef.current - 1);
                if (loadMoreInflightRef.current === 0) setLoadingMore(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isListRoute, page, limit, sortField, sortOrder, rows.length, debouncedFiltersClean, allRowsLoaded, lookupReady]);

    // NOTE: intentionally excludes `limit` — the frozen row order must stay stable when the user
    // changes rows-per-page, so only sort + filters define the order signature.
    const pageSnapshotSig = useMemo(() => {
        const entries = Object.entries(debouncedFiltersClean || {})
            .filter(([, v]) => String(v || "").trim())
            .sort((a, b) => String(a[0]).localeCompare(String(b[0])));
        const parts = entries.map(([k, v]) => `${k}=${String(v ?? "")}`);
        return `${sortField}|${sortOrder}|${parts.join("&")}`;
    }, [sortField, sortOrder, debouncedFiltersClean]);

    // When filters are cleared, reset paging + ensure at least first page cached
    useEffect(() => {
        if (!isListRoute) return;

        const activeFilterCount = Object.values(filters).filter((v) => String(v || "").trim()).length;
        if (activeFilterCount === 0) {
            if (page !== 1) setPage(1);
            if (limit !== MIN_PAGE_SIZE) setLimit(MIN_PAGE_SIZE);
            setFullRows(null);
            setRows([]);
            setSelected([]);
            tbTotalRef.current = 0;
            hingeTotalRef.current = 0;
            cachedWindowRef.current = 0;
            resetPagingCursors();
            setAllRowsLoaded(false);
            searchPrimedRef.current = false;
            firstFilterKeyRef.current = "";
            lastEditedFilterKeyRef.current = "";
            searchSignatureRef.current = "";
            const needed = MIN_PAGE_SIZE;
            fetchDefaultWindow(needed);
        }
        // Depend on `filters` (not debouncedFiltersClean): debouncedFiltersClean gets a new
        // object identity on every debounce tick, which re-fired this reset ~650ms after load
        // and wiped the freshly loaded rows (race → "no results found").
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, lookupReady]);

    // When the sort changes in default (no-filter) mode, the already-loaded rows were fetched
    // under the previous backend order, so resuming paging from where we left off would silently
    // skip records that now belong earlier in the list. Force a clean reload under the new sort.
    const sortResetSkipRef = useRef(true);
    useEffect(() => {
        if (!isListRoute) return;
        if (sortResetSkipRef.current) {
            sortResetSkipRef.current = false;
            return;
        }
        const activeFilterCount = Object.values(filters).filter((v) => String(v || "").trim()).length;
        if (activeFilterCount > 0) return;

        setFullRows(null);
        setRows([]);
        setSelected([]);
        tbTotalRef.current = 0;
        hingeTotalRef.current = 0;
        cachedWindowRef.current = 0;
        resetPagingCursors();
        setAllRowsLoaded(false);
        fetchDefaultWindow(Math.max(MIN_PAGE_SIZE, page * limit));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortField, sortOrder]);

    /* ===== Search-mode: full-load once when filters become active ===== */
    useEffect(() => {
        if (!isListRoute) return;

        let cancelled = false;

        const count = Object.values(debouncedFiltersClean).filter((v) => String(v || "").trim()).length;
        const prevCount = prevFilterCountRef.current;

        const primaryKeyGuess =
            firstFilterKeyRef.current ||
            lastEditedFilterKeyRef.current ||
            Object.keys(debouncedFiltersClean).find((k) => String(debouncedFiltersClean[k] || "").trim()) ||
            "";

        const statusNeedle = String(debouncedFiltersClean.status || "").trim().toLowerCase();
        const workerNeedle = String(debouncedFiltersClean.full_name || "").trim();
        const vendorNeedle = String(debouncedFiltersClean.vendor || "").trim();

        const hasTooShortTextFilter =
            (workerNeedle.length > 0 && workerNeedle.length < MIN_SEARCH_CHARS) ||
            (vendorNeedle.length > 0 && vendorNeedle.length < MIN_SEARCH_CHARS);

        if (count > 0 && hasTooShortTextFilter) {
            toast.warn("Please enter at least 4 characters to search", {
                toastId: "min4-global-approvals-filters",
                containerId: GLOBAL_TOAST_CONTAINER_ID,
            });
            prevFilterCountRef.current = count;
            return () => {
                cancelled = true;
            };
        }

        const isActiveLike =
            primaryKeyGuess === "status" &&
            statusNeedle.length >= 2 &&
            ("active".startsWith(statusNeedle) || "inactive".startsWith(statusNeedle) || "terminated".startsWith(statusNeedle));
        // "terminated".startsWith(statusNeedle);
        const firstKey = primaryKeyGuess;
        // const firstIsWorker = firstKey === "full_name" && workerNeedle.length > 0;
        /* Require a minimum of 4 chars for name/vendor text hinge searches API */
        const firstIsWorker = firstKey === "full_name" && workerNeedle.length > 3;
        const firstIsVendorOrDate = ["vendor", "start_date", "end_date"].includes(firstKey);

        const canHingeSearch = !!String(TB_HINGE_SEARCH_URL || "").trim();
        const canTBSearch = !!String(TB_SEARCH_URL || "").trim();
        const useHingeList = isActiveLike;
        const useHingeSearch = !useHingeList && firstIsWorker && canHingeSearch;
        const useTBSearch = !useHingeList && firstIsWorker && canTBSearch;
        const useTB = !useHingeList && !useHingeSearch && !useTBSearch;
        const useHinge = useHingeList || useHingeSearch || firstIsVendorOrDate;

        const primarySig = JSON.stringify({
            primaryKey: primaryKeyGuess,
            statusNeedle,
            workerNeedle,
            vendor: String(debouncedFiltersClean.vendor || "").trim(),
            start_date: String(debouncedFiltersClean.start_date || "").trim(),
            end_date: String(debouncedFiltersClean.end_date || "").trim(),
        });

        const shouldRun =
            count > 0 &&
            (!searchPrimedRef.current || searchSignatureRef.current !== primarySig || (prevCount === 0 && count > 0));

        if (shouldRun) {
            // Any filter search (worker name, vendor, start/end date, status) must wait for the
            // lookup API to finish loading. Applies to every role — no role checks.
            if (!isLookupReady()) {
                toast.warn("Search will be allowed after loading the configurations. Please wait…", {
                    toastId: "search-config-loading",
                    containerId: GLOBAL_TOAST_CONTAINER_ID,
                });
                prevFilterCountRef.current = count;
                return () => {
                    cancelled = true;
                };
            }
            (async () => {
                const thisLoadId = ++searchLoadIdRef.current;
                setLoading(true);
                setLoadingMore(false);
                try {
                    const lookupRaw = SS.getItem(K.lookup) || "{}";
                    let prof = {};
                    try {
                        prof = JSON.parse(lookupRaw);
                    } catch {
                        prof = {};
                    }

                    const storedRole = (SS.getItem(K.role) || "Nominator").trim();
                    const dtvId = prof?.dtvAnchorId || SS.getItem("tb_dtvid") || SS.getItem(K.email) || ENV.DEFAULT_DTVID || "";
                    const supplierName = prof?.supplierName || getVendor();

                    setFullRows([]);
                    setRows([]);
                    setPage(1);

                    let shownAny = false;
                    const showIfNeeded = () => {
                        if (shownAny) return;
                        shownAny = true;
                        setLoading(false);
                        setLoadingMore(true);
                    };

                    const p1Promises = [];

                    if (useTB || useTBSearch)
                        p1Promises.push(
                            (async () => {
                                const { rows: tbRows, total: tbTotal } = useTBSearch
                                    ? await fetchTBSearchOnce({ q: workerNeedle })
                                    : await fetchTBPage({ page: 1, limit: CHUNK, sortField, sortOrder });
                                if (cancelled || searchLoadIdRef.current !== thisLoadId) return;
                                if (!tbTotalRef.current) tbTotalRef.current = Number(tbTotal || 0);
                                const mappedTB = (tbRows || []).map(mapTBRowForUI);
                                if (mappedTB.length) {
                                    showIfNeeded();
                                    mergeIntoFullRows(mappedTB);
                                    setRows((prev) => mergeUniqueByKey(prev, mappedTB));
                                }
                            })()
                        );

                    if (ENABLE_HINGE && useHinge) {
                        p1Promises.push(
                            (async () => {
                                const { list: hingeList, total: hingeTotal } = useHingeSearch
                                    ? await fetchHingeSearchPage({ q: workerNeedle, page: 1, pageSize: CHUNK, roleName: storedRole, dtvId, supplierName })
                                    : await fetchHingePage({ page: 1, pageSize: CHUNK, roleName: storedRole, dtvId, supplierName });
                                if (cancelled || searchLoadIdRef.current !== thisLoadId) return;
                                if (!hingeTotalRef.current) hingeTotalRef.current = Number(hingeTotal || 0);
                                const mappedHinge = (hingeList || []).map(mapHingePersonToRow);
                                if (mappedHinge.length) {
                                    showIfNeeded();
                                    mergeIntoFullRows(mappedHinge);
                                    setRows((prev) => mergeUniqueByKey(prev, mappedHinge));
                                }
                            })()
                        );
                    }

                    const results = await Promise.allSettled(p1Promises);
                    const p1HadSuccess = results.some((r) => r.status === "fulfilled");
                    if (cancelled || searchLoadIdRef.current !== thisLoadId) return;

                    if (!shownAny && !p1HadSuccess) {
                        setNotice({ type: "error", message: "Failed to load approvals for search." });
                        setLoading(false);
                        setLoadingMore(false);
                        return;
                    }

                    (async () => {
                        try {
                            const tbTotal = Number(tbTotalRef.current || 0);
                            const hingeTotal = Number(ENABLE_HINGE ? (hingeTotalRef.current || 0) : 0);

                            const tbPages = Math.max(1, Math.ceil(tbTotal / CHUNK));
                            const hingePages = Math.max(1, Math.ceil(hingeTotal / CHUNK));

                            for (let p = 2; tbTotal > 0 && p <= tbPages; p++) {
                                if (cancelled || searchLoadIdRef.current !== thisLoadId) return;
                                await new Promise((r) => setTimeout(r, 0));
                                const { rows: pageRows } = await fetchTBPage({
                                    page: p,
                                    limit: Math.min(CHUNK, tbTotal - (p - 1) * CHUNK),
                                    sortField,
                                    sortOrder,
                                });
                                if (cancelled || searchLoadIdRef.current !== thisLoadId) return;
                                const mapped = (pageRows || []).map(mapTBRowForUI);
                                if (mapped.length) {
                                    mergeIntoFullRows(mapped);
                                    setRows((prev) => mergeUniqueByKey(prev, mapped));
                                }
                            }

                            if (ENABLE_HINGE && hingeTotal > 0) {
                                for (let p = 2; p <= hingePages; p++) {
                                    if (cancelled || searchLoadIdRef.current !== thisLoadId) return;
                                    await new Promise((r) => setTimeout(r, 0));
                                    const { list } = useHingeSearch
                                        ? await fetchHingeSearchPage({
                                            q: workerNeedle,
                                            page: p,
                                            pageSize: Math.min(CHUNK, hingeTotal - (p - 1) * CHUNK),
                                            roleName: storedRole,
                                            dtvId,
                                            supplierName,
                                        })
                                        : await fetchHingePage({
                                            page: p,
                                            pageSize: Math.min(CHUNK, hingeTotal - (p - 1) * CHUNK),
                                            roleName: storedRole,
                                            dtvId,
                                            supplierName,
                                        });
                                    if (cancelled || searchLoadIdRef.current !== thisLoadId) return;
                                    const mapped = (list || []).map(mapHingePersonToRow);
                                    if (mapped.length) {
                                        mergeIntoFullRows(mapped);
                                        setRows((prev) => mergeUniqueByKey(prev, mapped));
                                    }
                                }
                            }
                        } catch (bgErr) {
                            if (!cancelled && searchLoadIdRef.current === thisLoadId) {
                                console.error("Approvals background load failed:", bgErr);
                                setNotice({ type: "warn", message: "Loaded partial results. Some pages failed to load." });
                            }
                        } finally {
                            if (!cancelled && searchLoadIdRef.current === thisLoadId) {
                                setLoadingMore(false);
                                searchPrimedRef.current = true;
                            }
                        }
                    })();
                } catch (e) {
                    if (!cancelled) {
                        console.error("Approvals search load failed:", e);
                        setNotice({ type: "error", message: "Failed to load approvals for search." });
                    }
                } finally {
                    if (!cancelled && searchLoadIdRef.current === thisLoadId) setLoading(false);
                }
            })();
        }

        prevFilterCountRef.current = count;

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedFiltersClean, sortField, sortOrder, isListRoute, lookupReady]);

    /* ===== Sorting & filtering in-memory ===== */
    const fold = (s) =>
        String(s ?? "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

    const activeFilters = useMemo(() => {
        const min4Fields = new Set(["full_name", "vendor"]);
        return Object.fromEntries(
            Object.entries(filters).filter(([k, v]) => {
                const s = String(v || "").trim();
                if (!s) return false;
                if (min4Fields.has(k) && s.length < 4) return false;
                return true;
            })
        );
    }, [filters]);

    const sortedFilteredRows = useMemo(() => {
        const src = (fullRows && fullRows.length ? fullRows : rows) || [];

        const normalizeDateNeedle = (val) => {
            const raw = String(val ?? "").trim();
            if (!raw) return "";

            // MM-DD-YYYY -> MM/DD/YYYY
            const mdyDash = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
            if (mdyDash) return `${mdyDash[1]}/${mdyDash[2]}/${mdyDash[3]}`;

            // YYYY-MM-DD -> MM/DD/YYYY
            const fmt = formatDateMMDDYYYY(raw);
            if (fmt && fmt !== raw) return fmt;

            return raw.replace(/-/g, "/");
        };

        const getFilterHaystack = (row, key) => {
            if (key === "start_date" || key === "end_date") return formatDateMMDDYYYY(row?.[key]);
            if (key === "status") return normalizeStatus(row?.[key]);
            return row?.[key];
        };

        const getFilterNeedle = (key, needle) => {
            if (key === "start_date" || key === "end_date") return normalizeDateNeedle(needle);
            if (key === "status") return normalizeStatus(needle);
            return needle;
        };

        const filtered = Object.keys(activeFilters).length
            ? src.filter((r) =>
                Object.entries(activeFilters).every(([k, needle]) =>
                    fold(getFilterHaystack(r, k)).includes(fold(getFilterNeedle(k, needle)))
                )
            )
            : src;

        const isSearchMode = Object.keys(activeFilters).length > 0;
        const dir = sortOrder === "asc" ? 1 : -1;

        // Default "last activity" ordering uses the timestamp each source actually provides:
        //  - TB (manage-request): updated_at on initial load; created_at (npw-search) while searching
        //  - Hinge (list/search): createdTs
        // On equal timestamps, TB rows rank ahead of Hinge rows.
        const isLastActivitySort = ["updated_at", "updatedat"].includes(String(sortField || "").trim().toLowerCase());
        const isHingeRow = (row) => String(row?._source || "").toUpperCase() === "HINGE";
        const activityTsRaw = (row) => {
            if (isHingeRow(row)) return row?.createdTs || row?.created_at || row?.createdAt || row?.created || "";
            return isSearchMode
                ? (row?.created_at || row?.createdAt || row?.createdTs || row?.created || "")
                : (row?.updated_at || row?.updatedAt || row?.created_at || row?.createdAt || "");
        };
        const toMs = (val) => {
            const s = String(val ?? "").trim();
            if (!s) return null;
            if (/^\d{13}$/.test(s)) return Number(s);
            if (/^\d{10}$/.test(s)) return Number(s) * 1000;
            const t = Date.parse(s);
            return Number.isNaN(t) ? null : t;
        };

        const getVal = (row) => {
            const v = row?.[sortField];
            const s = v == null ? "" : String(v);
            if (s) return s;

            const sf = String(sortField || "").trim().toLowerCase();
            if (sf === "created_at" || sf === "createdat") {
                return String(
                    row?.created_at ||
                    row?.createdAt ||
                    row?.createdTs ||
                    row?.created ||
                    row?.updated_at ||
                    row?.updatedAt ||
                    ""
                );
            }
            return "";
        };

        const tryParseTime = (val) => {
            const t = Date.parse(String(val || ""));
            return Number.isNaN(t) ? null : t;
        };

        const tryParseNum = (val) => {
            const n = Number(String(val ?? "").trim());
            return Number.isFinite(n) ? n : null;
        };

        const cmp = (a, b) => {
            if (isLastActivitySort) {
                const ma = toMs(activityTsRaw(a));
                const mb = toMs(activityTsRaw(b));
                if (ma !== mb) {
                    if (ma === null) return 1;
                    if (mb === null) return -1;
                    return ma < mb ? -1 * dir : 1 * dir;
                }
                // Equal timestamp → TB ranks ahead of Hinge.
                const aH = isHingeRow(a) ? 1 : 0;
                const bH = isHingeRow(b) ? 1 : 0;
                if (aH !== bH) return aH - bH;
                return rowKey(a).localeCompare(rowKey(b));
            }

            const va = getVal(a);
            const vb = getVal(b);

            const ta = tryParseTime(va);
            const tb = tryParseTime(vb);
            if (ta !== null || tb !== null) {
                if (ta === null) return 1;
                if (tb === null) return -1;
                if (ta !== tb) return ta < tb ? -1 * dir : 1 * dir;
            }

            const na = tryParseNum(va);
            const nb = tryParseNum(vb);
            if (na !== null || nb !== null) {
                if (na === null) return 1;
                if (nb === null) return -1;
                if (na !== nb) return na < nb ? -1 * dir : 1 * dir;
            }

            const sc = String(va).localeCompare(String(vb)) * dir;
            if (sc) return sc;

            // Stable tie-breaker: ensures pagination never reshuffles when timestamps match.
            const kc = rowKey(a).localeCompare(rowKey(b));
            if (kc) return kc;

            // TB ranks ahead of Hinge as the final tie-breaker.
            return (isHingeRow(a) ? 1 : 0) - (isHingeRow(b) ? 1 : 0);
        };

        const arr = filtered.slice();
        try {
            arr.sort(cmp);
        } catch { }
        return arr;
    }, [rows, fullRows, activeFilters, sortField, sortOrder]);

    /* ===== Export (CSV for Excel) ===== */
    const [exporting, setExporting] = useState(false);

    const csvEscape = (v) => {
        const s = v == null ? "" : String(v);
        const needsQuote = /[",\n\r\t]/.test(s);
        const out = s.replace(/"/g, '""');
        return needsQuote ? `"${out}"` : out;
    };

    const rowsToCsv = (dataRows, cols) => {
        const headers = cols.map((c) => csvEscape(c.label));
        const lines = [headers.join(",")];

        for (const r of dataRows) {
            const line = cols.map((c) => csvEscape(r?.[c.field]));
            lines.push(line.join(","));
        }
        return "\ufeff" + lines.join("\r\n");
    };

    const downloadTextFile = (filename, text, mime = "text/csv;charset=utf-8") => {
        try {
            const blob = new Blob([text], { type: mime });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            console.error("download failed", e);
            setNotice({ type: "error", message: "Export failed." });
        }
    };

    const loadAllForExport = async () => {
        if (fullRows && fullRows.length) return fullRows;
        if (Object.keys(activeFilters).length) return fullRows && fullRows.length ? fullRows : rows;

        const lookupRaw = SS.getItem(K.lookup) || "{}";
        let prof = {};
        try {
            prof = JSON.parse(lookupRaw);
        } catch {
            prof = {};
        }

        const storedRole = (SS.getItem(K.role) || "Nominator").trim();
        const dtvId =
            prof?.dtvAnchorId ||
            SS.getItem("tb_dtvid") ||
            SS.getItem(K.email) ||
            ENV.DEFAULT_DTVID ||
            "";
        const supplierName = prof?.supplierName || getVendor();

        let tbTotal = tbTotalRef.current || 0;
        let hingeTotal = hingeTotalRef.current || 0;

        if (!tbTotal || !hingeTotal) {
            const [{ rows: tbRows, total: tbt }, hingeRes] = await Promise.all([
                fetchTBPage({ page: 1, limit: CHUNK, sortField, sortOrder }),
                fetchHingePage({ page: 1, pageSize: CHUNK, roleName: storedRole, dtvId, supplierName }),
            ]);
            tbTotal = tbTotal || tbt || (tbRows?.length || 0);
            hingeTotal = hingeTotal || hingeRes.total || 0;
            if (!tbTotalRef.current) tbTotalRef.current = tbTotal;
            if (!hingeTotalRef.current) hingeTotalRef.current = hingeTotal;
        }

        const tbPages = Math.max(1, Math.ceil(tbTotal / CHUNK));
        const hingePages = Math.max(1, Math.ceil(hingeTotal / CHUNK));

        const tbAll = [];
        for (let p = 1; tbTotal > 0 && p <= tbPages; p++) {
            await new Promise((r) => setTimeout(r, 0));
            const { rows: pageRows } = await fetchTBPage({
                page: p,
                limit: Math.min(CHUNK, tbTotal - (p - 1) * CHUNK),
                sortField,
                sortOrder,
            });
            if (pageRows?.length) tbAll.push(...pageRows);
        }

        let hingeAll = [];
        if (hingeTotal > 0) {
            for (let p = 1; p <= hingePages; p++) {
                await new Promise((r) => setTimeout(r, 0));
                const { list } = await fetchHingePage({
                    page: p,
                    pageSize: Math.min(CHUNK, hingeTotal - (p - 1) * CHUNK),
                    roleName: storedRole,
                    dtvId,
                    supplierName,
                });
                if (Array.isArray(list) && list.length) hingeAll = hingeAll.concat(list);
            }
        }

        const combined = tbAll.concat(hingeAll.map(mapHingePersonToRow));
        setFullRows(combined);
        setRows(combined);
        return combined;
    };

    const onExport = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            let data = [];
            if (selected && selected.length > 0) {
                // Export only selected rows
                const dataset = (fullRows && fullRows.length ? fullRows : rows);
                const selectedKeys = new Set(selected.map((s) => s._key));
                data = dataset.filter((r) => selectedKeys.has(rowKey(r)));
            } else {
                // Export all (filtered or full)
                const all = await loadAllForExport();
                data = (Object.keys(activeFilters).length ? sortedFilteredRows : all) || [];
            }
            const csv = rowsToCsv(data, COLUMNS_EXPORT);
            const ts = new Date().toISOString().slice(0, 10);
            downloadTextFile(`approvals_${ts}.csv`, csv);
        } catch (e) {
            console.error(e);
            setNotice({ type: "error", message: "Export failed." });
        } finally {
            setExporting(false);
        }
    };

    /* ===== Totals & client slicing (footer) ===== */
    const hasFilters = Object.keys(activeFilters).length > 0;

    const footerTotal = useMemo(() => {
        if (hasFilters) return Math.max(1, sortedFilteredRows.length || 1);
        const sourceRows = (fullRows && fullRows.length ? fullRows : rows) || [];
        const total = (tbTotalRef.current || 0) + (hingeTotalRef.current || 0);
        // While loading, use the optimistic raw TB+Hinge total; once everything is loaded,
        // correct down to the actual unique merged row count so pagination doesn't overshoot.
        if (allRowsLoaded) return Math.max(1, sourceRows.length || 1);
        return Math.max(1, total || sourceRows.length || 1);
    }, [hasFilters, sortedFilteredRows.length, fullRows, rows, allRowsLoaded]);

    const totalPages = useMemo(() => Math.max(1, Math.ceil(footerTotal / Math.max(1, limit))), [footerTotal, limit]);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [totalPages, page]);

    // Freeze a single global row order per sort/filter signature (independent of page size).
    // Rows already shown stay put; newly loaded rows only ever append to the unlocked tail in
    // sorted order. Because the order doesn't depend on `limit`, changing rows-per-page simply
    // re-slices the same sequence and never reshuffles what the user already saw.
    const clientPagedRows = useMemo(() => {
        const start = (page - 1) * limit;
        const end = start + limit;

        if (lockedPagesRef.current.sig !== pageSnapshotSig) {
            lockedPagesRef.current = { sig: pageSnapshotSig, keys: [] };
        }

        const byKey = new Map(sortedFilteredRows.map((r) => [rowKey(r), r]));
        const sortedKeys = sortedFilteredRows.map((r) => rowKey(r));

        // Keep the previously-frozen keys (dropping any that no longer exist), then append the
        // remaining current keys in sorted order.
        const frozen = lockedPagesRef.current.keys.filter((k) => byKey.has(k));
        const frozenSet = new Set(frozen);
        const tail = sortedKeys.filter((k) => !frozenSet.has(k));
        const order = frozen.concat(tail);

        // Extend the frozen prefix to cover the current page once those rows are settled
        // (everything loaded, or we already have a full page's worth beyond the prefix). Only
        // ever grow it — never shrink — so reducing rows-per-page can't release already-frozen
        // rows back into the (re-sortable) tail.
        const targetLen = allRowsLoaded ? order.length : (order.length >= end ? end : frozen.length);
        lockedPagesRef.current.keys = order.slice(0, Math.max(frozen.length, targetLen));

        return order
            .slice(start, end)
            .map((k) => byKey.get(k))
            .filter(Boolean);
    }, [sortedFilteredRows, page, limit, pageSnapshotSig, allRowsLoaded]);

    //console.log("Render with client paged rows:", clientPagedRows.length, "total filtered:", sortedFilteredRows.length, "rows", rows.length, "fullRows:", fullRows ? fullRows.length : null);

    const startIndex = useMemo(() => (clientPagedRows.length ? (page - 1) * limit + 1 : 0), [page, limit, clientPagedRows.length]);
    const endIndex = useMemo(
        () => (clientPagedRows.length ? Math.min(startIndex + limit - 1, footerTotal) : 0),
        [startIndex, limit, footerTotal, clientPagedRows.length]
    );

    const pageNumbers = useMemo(() => {
        const max = 7;
        let start = Math.max(1, page - Math.floor(max / 2));
        let end = start + max - 1;
        if (end > totalPages) {
            end = totalPages;
            start = Math.max(1, end - max + 1);
        }
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }, [page, totalPages]);

    /* ===== Selection (checkboxes) ===== */
    // Clear selection when filters or sort change, but not on page/limit change
    useEffect(() => setSelected([]), [filters, sortField, sortOrder]);

    const toggleRow = (row) => {
        const k = rowKey(row);
        setSelected((prev) => {
            const hit = prev.find((s) => s._key === k);
            if (hit) return prev.filter((s) => s._key !== k);

            return [
                ...prev,
                {
                    _key: k,
                    npw_id: row.npw_id,
                    status: row.status,
                    security_id: row.security_id,
                    full_name: row.full_name || `${row.first_name || ""} ${row.last_name || ""}`.trim(),
                    first_name: row.first_name,
                    last_name: row.last_name,
                    dtvuid: row.dtvuid,
                    attuid: row.attuid,
                    email: row.email,
                },
            ];
        });
    };

    // --- Select All logic (like ManageRequest.js) ---
    const currentKeys = useMemo(() => clientPagedRows.map((r) => rowKey(r)), [clientPagedRows]);
    const allChecked = currentKeys.length > 0 && currentKeys.every((k) => selected.some((s) => s._key === k));
    const toggleAll = () => {
        if (allChecked) {
            setSelected((prev) => prev.filter((s) => !currentKeys.includes(s._key)));
        } else {
            const add = clientPagedRows.map((r) => ({
                _key: rowKey(r),
                npw_id: r.npw_id,
                status: r.status,
                security_id: r.security_id,
                full_name: r.full_name || `${r.first_name || ""} ${r.last_name || ""}`.trim(),
                first_name: r.first_name,
                last_name: r.last_name,
                dtvuid: r.dtvuid,
                attuid: r.attuid,
                email: r.email,
            }));
            setSelected((prev) => {
                const seen = new Set(prev.map((x) => x._key));
                const merged = [...prev];
                add.forEach((a) => {
                    if (!seen.has(a._key)) merged.push(a);
                });
                return merged;
            });
        }
    };

    /* ===== Manage-style enablement (Edit / Offboard) ===== */
    const oneSelected = selected.length === 1;
    const sel = oneSelected ? selected[0] : null;
    const selStatus = sel ? normalizeStatus(sel.status) : "";
    const selFullName = sel?.full_name || `${sel?.first_name || ""} ${sel?.last_name || ""}`.trim();

    const EDIT_ALLOWED_STATUSES = new Set([
        "active",
        "inactive",
        "draft",
        "approved",
        "submitted",
        "inforequested",
        "terminated",
        "submitted to cp",
        "offboarding submitted",
        "offboarding submitted to cp"
        //"failed",
    ]);
    const hasAnyId = !!(sel?.npw_id || sel?.dtvuid || sel?.attuid || sel?.email);
    const canEdit = canManageHere && oneSelected && hasAnyId && EDIT_ALLOWED_STATUSES.has(selStatus);
    const canOffboard = canManageHere && oneSelected && selStatus === "active" && hasAnyId;

    useEffect(() => {
        if (offboardIdType === "ATTUID" && !(sel?.attuid || "")) {
            setOffboardIdType("DTVID");
        }
    }, [offboardIdType, sel]);

    /* ===== Navigation (mark return + persist state) ===== */
    const persistNow = () => saveApState({ page, limit, sortField, sortOrder, filters });

    const openRow = (r) => {
        const st = normalizeStatus(r?.status);

        // Draft rows should open the onboarding edit flow.
        if (st === "draft") {
            persistNow();
            try {
                SS.setItem("npw.search.returnPath", "/approvals");
            } catch { }

            const from =
                `${location.pathname || ""}${location.search || ""}${location.hash || ""}` ||
                location.pathname ||
                "/approvals";

            const dtvish = r?.dtvuid || r?.attuid || r?.email || "";
            const path = r?.npw_id
                ? `/onboarding/new/${encodeURIComponent(r.npw_id)}`
                : dtvish
                    ? `/onboarding/new/${encodeURIComponent(`DTVID=${dtvish}`)}`
                    : "";
            if (path) navigate(path, { state: { from } });
            return;
        }

        persistNow();
        markReturnFromDetail();

        try {
            SS.setItem("npw.search.returnPath", "/approvals");
        } catch { }

        if (r.npw_id) {
            navigate(`/approvals/${r.npw_id}`);
            return;
        }
        const dtvish = r.dtvuid || r.attuid || r.email || "";
        if (dtvish) navigate(`/approvals/${encodeURIComponent(`DTVID=${dtvish}`)}`);
    };

    const openEditFromSelection = () => {
        if (!canEdit) {
            setNotice({ type: "error", message: "Select exactly one eligible row to edit." });
            return;
        }
        const dataset = (fullRows && fullRows.length ? fullRows : rows);
        const row = dataset.find((r) => rowKey(r) === sel?._key) || dataset.find((r) => String(r?.npw_id || "") === String(sel?.npw_id || ""));
        if (!row) {
            setNotice({ type: "error", message: "Selected row not found in the current list." });
            return;
        }

        try {
            SS.setItem("npw.search.returnPath", "/approvals");
        } catch { }

        const from =
            `${location.pathname || ""}${location.search || ""}${location.hash || ""}` ||
            location.pathname ||
            "/approvals";
        const dtvish = row.dtvuid || row.attuid || row.email || "";
        const path = row.npw_id
            ? `/onboarding/new/${encodeURIComponent(row.npw_id)}`
            : dtvish
                ? `/onboarding/new/${encodeURIComponent(`DTVID=${dtvish}`)}`
                : "";
        if (!path) {
            setNotice({ type: "error", message: "Selected row does not have an NPWID or DTVID/ATTUID to edit." });
            return;
        }
        navigate(path, { state: { from } });
    };

    const openOffboardModal = () => {
        if (!canOffboard) {
            setNotice({ type: "error", message: "Select exactly one Active row to offboard." });
            return;
        }
        setOffboardError("");
        setOffboardServerError("");
        setNote("");
        setEndDate("");
        setOffboardIdType("DTVID");
        setIsModalOpen(true);
    };

    const refreshAfterMutation = async () => {
        tbTotalRef.current = 0;
        hingeTotalRef.current = 0;
        cachedWindowRef.current = 0;
        resetPagingCursors();
        setAllRowsLoaded(false);
        lockedPagesRef.current = { sig: "", keys: [] };
        setRows([]);
        setFullRows(null);
        searchPrimedRef.current = false;
        prevFilterCountRef.current = 0;

        const activeFilterCount = Object.values(filters).filter((v) => String(v || "").trim()).length;
        if (activeFilterCount > 0) {
            setFilters((p) => ({ ...p }));
            return;
        }
        const quantum = Math.max(MIN_PAGE_SIZE, limit * PREFETCH_PAGES);
        const needed = Math.max(MIN_PAGE_SIZE, Math.ceil((page * limit) / quantum) * quantum);
        await fetchDefaultWindow(needed);
    };

    /* ===== Action button (first pending/submitted) ===== */
    const pendingSelected = useMemo(() => {
        return selected.find((s) => {
            const k = normalizeStatus(s?.status);
            // return ["pending", "submitted", "submitted to cp", "offboarding submitted", "offboarding submitted to cp"].includes(k);
            /* Take action enabled for all status */
            return K;
        });
    }, [selected]);

    const goToAction = () => {
        if (selected.length > 1) {
            setNotice({ type: "error", message: "Select exactly one Pending/Submitted row to take action." });
            return;
        }
        if (selected.length > 0) {
            if (pendingSelected) {
                const dataset = (fullRows && fullRows.length ? fullRows : rows) || [];
                const row = dataset.find((r) => rowKey(r) === pendingSelected._key);
                if (row) {
                    persistNow();
                    markReturnFromDetail();
                    openRow(row);
                    return;
                }

                const dtvish = pendingSelected.dtvuid || pendingSelected.attuid || pendingSelected.email || "";
                if (pendingSelected.npw_id) {
                    persistNow();
                    markReturnFromDetail();

                    try {
                        SS.setItem("npw.search.returnPath", "/approvals");
                    } catch { }

                    navigate(`/approvals/${pendingSelected.npw_id}`);
                    return;
                }
                if (dtvish) {
                    persistNow();
                    markReturnFromDetail();

                    try {
                        SS.setItem("npw.search.returnPath", "/approvals");
                    } catch { }

                    navigate(`/approvals/${encodeURIComponent(`DTVID=${dtvish}`)}`);
                    return;
                }
            }
            setNotice({ type: "error", message: "Select a Pending/Submitted row to take action." });
            return;
        }

        const firstPending = sortedFilteredRows.find((r) => {
            const k = normalizeStatus(r?.status);
            return k === "pending" || k === "submitted";
        });
        if (firstPending?.npw_id) {
            persistNow();
            markReturnFromDetail();

            try {
                SS.setItem("npw.search.returnPath", "/approvals");
            } catch { }

            navigate(`/approvals/${firstPending.npw_id}`);
            return;
        }
        setNotice({ type: "error", message: "No Pending/Submitted rows available." });
    };

    /* ===== Viewport height rule (as in Manage Requests) ===== */
    const ROW_BORDER_PX = 1;
    const EXTRA_BUFFER_PX = 10;
    const viewportStyle =
        limit > MIN_PAGE_SIZE && footerTotal > limit
            ? {
                height: `calc(var(--mr-head-h, 32px) + (${limit}) * (var(--mr-row-h, 32px) + ${ROW_BORDER_PX}px) + ${EXTRA_BUFFER_PX}px)`,
            }
            : undefined;
    const viewportClass = `table-viewport ${limit > MIN_PAGE_SIZE ? "scroll" : "no-scroll"} table-wrap`;
    const TABLE_FONT_SIZE = 11;

    return (
        <AppShell>
            <div className="approvals-page">
                <div className="mr-wrap zoom-90">
                    {/* Page title + toolbar */}
                    <div className="ap-head">
                        <div className="mr-titleblock">
                            <div className="mr-title">Approvals</div>
                            <div className="mr-sub">View and manage approvals for workers.</div>
                            {loadingMore && !loading ? (
                                <div style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>Loading more results…</div>
                            ) : null}
                        </div>
                        <div className="ap-toolbar">
                            <button
                                className="btn-primary"
                                onClick={goToAction}
                                disabled={
                                    !oneSelected || !pendingSelected

                                    // selected.length > 0
                                    //     ? !pendingSelected
                                    //     : !sortedFilteredRows.some((r) => {
                                    //         const k = normalizeStatus(r?.status);
                                    //         return k === "pending" || k === "submitted";
                                    //     })
                                }
                            >
                                Take Action
                            </button>

                            <button className="btn btn-ghost" onClick={onExport} disabled={exporting || loading}>
                                {exporting ? "Exporting…" : "Export"}
                            </button>

                            {canManageHere && (
                                <>
                                    <button
                                        className="btn-sub"
                                        onClick={openOffboardModal}
                                        disabled={!canOffboard || !OFFBOARD_URL}
                                        title={!OFFBOARD_URL ? "OFFBOARD_URL not configured" : ""}
                                    >
                                        Off Board Worker
                                    </button>
                                    <button className="btn-sub" onClick={openEditFromSelection} disabled={!canEdit}>
                                        Edit
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Inline notice */}
                    {notice?.message && <Notice type={notice.type} message={notice.message} onClose={() => setNotice(null)} />}

                    {/* Card: table + footer (pager/per-page) */}
                    <div className="card mr-card-tight">
                        <div className={viewportClass} style={viewportStyle}>
                            <table className="mr-table white-head tight zebra fixed" style={{ fontSize: TABLE_FONT_SIZE }}>
                                <thead>
                                    <tr className="head-row">
                                        <th className="col-select">
                                            <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                                        </th>
                                        {COLUMNS.map((c) => (
                                            <th
                                                key={c.field}
                                                onClick={() => {
                                                    setPage(1);
                                                    if (c.field === sortField) setSortOrder((p) => (p === "asc" ? "desc" : "asc"));
                                                    else {
                                                        setSortField(c.field);
                                                        setSortOrder("asc");
                                                    }
                                                    const inp = filterInputRefs.current[c.field];
                                                    if (inp) inp.focus();
                                                }}
                                                className={`sortable ${c.field === "full_name"
                                                    ? "col-name"
                                                    : c.field === "status"
                                                        ? "col-status"
                                                        : c.field === "start_date" || c.field === "end_date"
                                                            ? "col-date"
                                                            : c.field === "vendor"
                                                                ? "col-vendor"
                                                                : ""
                                                    }`}
                                                title={`Sort by ${c.label}`}
                                            >
                                                <span>{c.label}</span>
                                                <span className={`sort-caret ${sortField === c.field ? (sortOrder === "asc" ? "asc" : "desc") : ""}`} />
                                            </th>
                                        ))}
                                    </tr>

                                    {/* Filter row */}
                                    <tr className="filter-row">
                                        <th className="col-select" />
                                        {COLUMNS.map((c) => (
                                            <th
                                                key={`${c.field}-filter`}
                                                className={`filter-cell ${c.field === "full_name"
                                                    ? "col-name"
                                                    : c.field === "status"
                                                        ? "col-status"
                                                        : c.field === "start_date" || c.field === "end_date"
                                                            ? "col-date"
                                                            : c.field === "vendor"
                                                                ? "col-vendor"
                                                                : ""
                                                    }`}
                                            >
                                                <div className="filter-input-wrap">
                                                    <span className="icon">🔍</span>
                                                    <input
                                                        ref={(el) => {
                                                            filterInputRefs.current[c.field] = el;
                                                        }}
                                                        className="filter-input"
                                                        type="text"
                                                        placeholder={c.field === "full_name" || c.field === "vendor" ? "Search (min 4 chars)" : "Search"}
                                                        value={filterInputValues[c.field] || ""}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            const trimmed = String(v || "").trim();
                                                            const isMin4Field = c.field === "full_name" || c.field === "vendor";

                                                            setFilterInputValues((prev) => ({ ...prev, [c.field]: v }));

                                                            if (!isMin4Field) {
                                                                lastEditedFilterKeyRef.current = c.field;
                                                                if (!firstFilterKeyRef.current && trimmed) {
                                                                    firstFilterKeyRef.current = c.field;
                                                                }
                                                                setPage(1);
                                                                setFilters((prev) => ({ ...prev, [c.field]: v }));
                                                                return;
                                                            }

                                                            if (!trimmed) {
                                                                lastEditedFilterKeyRef.current = c.field;
                                                                if (!firstFilterKeyRef.current && trimmed) {
                                                                    firstFilterKeyRef.current = c.field;
                                                                }
                                                                setPage(1);
                                                                setFilters((prev) => ({ ...prev, [c.field]: "" }));
                                                                return;
                                                            }

                                                            if (!isValidSearchQuery(trimmed)) {
                                                                toast.warn("Please avoid special characters in your search", {
                                                                    toastId: "invalid-search-approvals",
                                                                    containerId: GLOBAL_TOAST_CONTAINER_ID,
                                                                });
                                                                return;
                                                            }

                                                            if (trimmed.length >= MIN_SEARCH_CHARS) {
                                                                lastEditedFilterKeyRef.current = c.field;
                                                                if (!firstFilterKeyRef.current && trimmed) {
                                                                    firstFilterKeyRef.current = c.field;
                                                                }
                                                                setPage(1);
                                                                // Keep raw value (with spaces) so multi-word searches work;
                                                                // trimming/min-4 gating is applied in getEffectiveFilters.
                                                                setFilters((prev) => ({ ...prev, [c.field]: v }));
                                                            }
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                {loading ? (
                                    <tbody className="tbody-loading">
                                        {Array.from({ length: limit }).map((_, i) => (
                                            <tr className="row-skeleton" key={`sk-${i}`}>
                                                <td className="col-select">
                                                    <span className="skel skel-checkbox" />
                                                </td>
                                                {COLUMNS.map((c, j) => (
                                                    <td key={`${i}-${j}`}>
                                                        {c.field === "status" ? <span className="skel-tag w-30" /> : <span className={`skel ${j % 2 ? "w-60" : "w-40"}`} />}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                ) : clientPagedRows.length === 0 ? (
                                    <tbody>
                                        <tr>
                                            <td colSpan={COLUMNS.length + 1}>
                                                <div className="empty">No data found.</div>
                                            </td>
                                        </tr>
                                    </tbody>
                                ) : (
                                    <tbody>
                                        {clientPagedRows.map((r, idx) => {
                                            const p = statusPill(r.status);
                                            const rowCls = rowClassForStatus(r.status);
                                            const rk = rowKey(r);
                                            return (
                                                <tr
                                                    key={rk} // ✅ FIX: stable unique key for TB + Hinge rows
                                                    className={rowCls}
                                                    onClick={(e) => {
                                                        if (!e.target.closest("input,button,a")) openRow(r);
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") openRow(r);
                                                    }}
                                                >
                                                    <td className="col-select" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selected.some((s) => s._key === rk)}
                                                            onChange={() => toggleRow(r)}
                                                            disabled={false} // ✅ ALWAYS enabled for TB + Hinge rows
                                                            aria-disabled={false}
                                                            title="Select row"
                                                        />
                                                    </td>

                                                    {COLUMNS.map((c) => {
                                                        const rawVal =
                                                            c.field === "full_name"
                                                                ? r.full_name || `${r.first_name || ""} ${r.last_name || ""}`.trim()
                                                                : c.field === "start_date" || c.field === "end_date"
                                                                    ? formatDateMMDDYYYY(r[c.field])
                                                                    : r[c.field];

                                                        let cell;
                                                        if (c.isId) {
                                                            cell = (
                                                                <IdPillButton
                                                                    text={maskSecurityId(r.security_id)}
                                                                    disabled={!r.npw_id}
                                                                    title={r.npw_id ? "Open details" : "No NPW detail for Hinge row"}
                                                                    onClick={() => r.npw_id && openRow(r)}
                                                                />
                                                            );
                                                        } else if (c.field === "status") {
                                                            cell = (
                                                                <span className="badge-wrap" title={r.status || "-"}>
                                                                    <span className={p.cls}>{p.label}</span>
                                                                </span>
                                                            );
                                                        } else if (c.isName) {
                                                            cell = (
                                                                <a
                                                                    className="cell-link name-pill"
                                                                    href="#"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        openRow(r);
                                                                    }}
                                                                    title={rawVal || "-"}
                                                                >
                                                                    {rawVal || "-"}
                                                                    <span className="source-chip"></span>
                                                                </a>
                                                            );
                                                        } else {
                                                            cell = (
                                                                <span className="cell-text" title={rawVal || "-"}>
                                                                    {rawVal || "-"}
                                                                </span>
                                                            );
                                                        }

                                                        const tdClasses = ["td-trunc"];
                                                        if (c.field === "full_name") tdClasses.push("col-name");
                                                        if (c.field === "status") tdClasses.push("col-status");
                                                        if (c.field === "start_date" || c.field === "end_date") tdClasses.push("col-date");
                                                        if (c.field === "vendor") tdClasses.push("col-vendor");

                                                        return (
                                                            <td key={`${rk}-${c.field}-${idx}`} className={tdClasses.join(" ")}>
                                                                {cell}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                )}
                            </table>
                        </div>

                        {/* Footer: count + Rows-per-page + pager */}
                        <div
                            className="mr-footer"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "8px 12px",
                                borderTop: "1px solid #e6e8eb",
                                background: "#fff",
                            }}
                        >
                            <div className="mr-count">{footerTotal ? `${startIndex}-${endIndex} of ${footerTotal}` : "0-0 of 0"}</div>

                            <div className="mr-pageline" style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                <div className="mr-perpage inline">
                                    <span>Rows per page</span>{" "}
                                    <select
                                        value={limit}
                                        onChange={(e) => {
                                            const n = Math.max(MIN_PAGE_SIZE, parseInt(e.target.value, 10) || MIN_PAGE_SIZE);
                                            setLimit(n);
                                            setPage(1);
                                        }}
                                    >
                                        {[10, 15, 20, 25, 30, 40, 50].map((n) => (
                                            <option key={n} value={n}>
                                                {n}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mr-pager">
                                    <button className="linklike" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                                        ‹
                                    </button>
                                    {pageNumbers.map((pg) => (
                                        <button key={pg} className={`linklike ${pg === page ? "active" : ""}`} onClick={() => setPage(pg)}>
                                            {pg}
                                        </button>
                                    ))}
                                    <button className="linklike" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                                        ›
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Offboard modal (for Sponsor / ESA / OPS Admin) */}
                    {isModalOpen && (
                        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="offboard-title">
                            <div className="modal">
                                <h2 id="offboard-title">Off Board Worker</h2>
                                <p>Are you sure you want to offboard this worker?</p>

                                <div className="field">
                                    <label htmlFor="offboard-worker-name">Worker Name</label>
                                    <input id="offboard-worker-name" type="text" value={selFullName || ""} disabled />
                                </div>

                                <div className="field">
                                    <label htmlFor="offboard-date">End Date</label>
                                    <input
                                        id="offboard-date"
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        min={new Date().toISOString().split("T")[0]}
                                    />
                                    {offBoardError && <div className="err">{offBoardError}</div>}
                                </div>

                                <div className="field">
                                    <label>Offboard By</label>
                                    <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                            <input
                                                type="checkbox"
                                                checked={offboardIdType === "DTVID"}
                                                onChange={() => setOffboardIdType("DTVID")}
                                            />
                                            <span>DTVID/ATTUID</span>
                                        </label>

                                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                            <input
                                                type="checkbox"
                                                checked={offboardIdType === "ATTUID"}
                                                onChange={() => setOffboardIdType("ATTUID")}
                                                disabled={!sel?.attuid}
                                                title={!sel?.attuid ? "ATTUID not available for selected row." : ""}
                                            />
                                            <span>ATTUID</span>
                                        </label>
                                    </div>
                                    {offboardIdType === "ATTUID" && !sel?.attuid && <div className="err">Selected row does not have an ATTUID.</div>}
                                </div>

                                <div className="field">
                                    <label htmlFor="offboard-notes">Notes (optional)</label>
                                    <textarea
                                        id="offboard-notes"
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        rows={3}
                                        placeholder="Add any context for the offboard request"
                                    />
                                </div>

                                {offBoardServerError && <div className="err">{offBoardServerError}</div>}

                                <div className="modal-actions modal-buttons">
                                    <button
                                        className="btn btn-ghost"
                                        onClick={() => {
                                            setIsModalOpen(false);
                                            setOffboardError("");
                                            setOffboardServerError("");
                                        }}
                                        disabled={offboarding}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-cta"
                                        onClick={async () => {
                                            if (!canOffboard) {
                                                setOffboardError("Select exactly one Active row.");
                                                return;
                                            }
                                            if (!endDate) {
                                                setOffboardError("Please select an end date.");
                                                return;
                                            }
                                            const today = new Date().toISOString().split("T")[0];
                                            if (new Date(endDate) < new Date(today)) {
                                                setOffboardError("Please select an end date greater than or equal to today.");
                                                return;
                                            }
                                            setOffboardError("");

                                            if (offboardIdType === "ATTUID" && !(sel?.attuid || "")) {
                                                setOffboardError("Selected row does not have an ATTUID.");
                                                return;
                                            }

                                            const actor = getActorDtvId();
                                            const actorName = getActorName();
                                            const performedAt = new Date().toISOString();
                                            const name = `${sel?.first_name || ""} ${sel?.last_name || ""}`.trim();
                                            const actorEmail = getActorEmail();

                                            try {
                                                if (!OFFBOARD_URL) throw new Error("OFFBOARD_URL is not configured.");
                                                setOffboardServerError("");
                                                setOffboarding(true);

                                                const common = {
                                                    end_date: endDate,
                                                    comments: note ?? "",
                                                    performed_by_dtv_id: actor || "",
                                                    performedName: actorName || actor || "",
                                                    performed_at: performedAt,
                                                    name,
                                                    actorEmail,
                                                    // --- Submitter identity (always derive from session in UI) ---
                                                    submitter_id: actorEmail || undefined,
                                                    submitter: actorEmail || undefined,
                                                    submitter_email: actorEmail || undefined,
                                                    submitter_name: actorName || actor || undefined,
                                                    submitterDtvid: actor || undefined,
                                                    submitter_dtvid: actor || undefined,
                                                    rawActorEmail: actorEmail || undefined,
                                                    action: "offboard",
                                                };

                                                const payload =
                                                    sel?.npw_id && sel?.security_id
                                                        ? {
                                                            ...common,
                                                            npw_id: sel.npw_id,
                                                            security_id: sel.security_id,
                                                            offboardIdType,
                                                            offboardByDtvId: offboardIdType === "DTVID",
                                                            offboardByAttuid: offboardIdType === "ATTUID",
                                                        }
                                                        : {
                                                            ...common,
                                                            dtv_id: offboardIdType === "ATTUID" ? sel?.dtvuid || "" : sel?.dtvuid || sel?.email || "",
                                                            offboardIdType,
                                                            offboardByDtvId: offboardIdType === "DTVID",
                                                            offboardByAttuid: offboardIdType === "ATTUID",
                                                        };

                                                if (!payload.npw_id && !payload.dtv_id) {
                                                    throw new Error("Selected row has neither NPWID nor DTVID to offboard by.");
                                                }

                                                await tbPostJson(OFFBOARD_URL, payload, { fetchInit: { method: "POST" } }); // PUT/POST

                                                setIsModalOpen(false);
                                                setNotice({ type: "success", message: "Off board request submitted." });
                                                await refreshAfterMutation();
                                            } catch (e) {
                                                console.error("Offboard failed:", e);
                                                const msg = extractOffboardError(e);
                                                setOffboardServerError(msg);
                                                setNotice({ type: "error", message: msg });
                                            } finally {
                                                setOffboarding(false);
                                            }
                                        }}
                                        disabled={offboarding || !canOffboard}
                                    >
                                        {offboarding ? "Submitting..." : "Submit"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </AppShell>
    );
}
